import React from 'react';
import { TaskReport } from '../../components/TaskReport';
import { ReportForm } from '../../components/ReportForm';
import { ReportHistory } from '../../components/ReportHistory';
import { FileSearch } from 'lucide-react';
import { fetchTaskDetails, fetchDocDetails } from '../../services/clickupApi';
import { getReportHistory, addReportToHistory, removeReportFromHistory } from '../../services/historyStorage';
import { env } from '../../config/env';
import type { ClickUpTask, ClickUpDoc } from '../../types/clickup';
import type { ReportHistoryItem } from '../../types/history';

interface ReportGeneratorProps {
  onBack: () => void;
}

const ReportGenerator: React.FC<ReportGeneratorProps> = ({ onBack }) => {
  const [task, setTask] = React.useState<ClickUpTask | null>(null);
  const [doc, setDoc] = React.useState<ClickUpDoc | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [docError, setDocError] = React.useState<string | null>(null);
  const [apiKey, setApiKey] = React.useState(env.defaultApiKey);
  const [showForm, setShowForm] = React.useState(true);
  const [history, setHistory] = React.useState(() => getReportHistory());

  // Parse URL parameters on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const taskId = params.get('taskId');
    const docId = params.get('docId');
    const pageId = params.get('pageId');

    if (taskId) {
      generateReport(taskId, docId, pageId);
    }
  }, []);

  // Update URL when report is generated
  const updateURL = (taskId: string, docId: string | null, pageId: string | null) => {
    const params = new URLSearchParams();
    params.set('taskId', taskId);
    if (docId) params.set('docId', docId);
    if (pageId) params.set('pageId', pageId);
    
    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newURL);
  };

  const generateReport = async (taskId: string, docId: string | null, pageId: string | null) => {
    setLoading(true);
    setError(null);
    setDocError(null);
    setTask(null);
    setDoc(null);
    
    try {
      const taskData = await fetchTaskDetails(taskId, apiKey);
      setTask(taskData);

      let docData = null;
      if (docId && pageId) {
        try {
          docData = await fetchDocDetails(docId, pageId, env.workspaceId, apiKey);
          setDoc(docData);
        } catch (docError) {
          setDocError(docError instanceof Error ? docError.message : 'Erro ao buscar o documento');
        }
      }

      // Update URL with parameters
      updateURL(taskId, docId, pageId);

      // Add to history
      const operationField = taskData.custom_fields?.find(f => f.name === '5 - Operação');
      const historyItem: ReportHistoryItem = {
        id: crypto.randomUUID(),
        taskId: taskData.id,
        docId: docId,
        pageId: pageId,
        taskName: taskData.name,
        operation: operationField,
        team: taskData.custom_fields?.find(f => f.name === '2- Equipe')?.value || null,
        date: new Date().toISOString(),
        customFields: taskData.custom_fields
      };
      
      addReportToHistory(historyItem);
      setHistory(getReportHistory());
      setShowForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocorreu um erro ao buscar a tarefa');
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = () => {
    generateReport(env.example.taskId, env.example.docId, env.example.pageId);
  };

  const handleHistoryItemClick = async (item: ReportHistoryItem) => {
    await generateReport(item.taskId, item.docId, item.pageId);
  };

  const handleHistoryItemRemove = (id: string) => {
    removeReportFromHistory(id);
    setHistory(getReportHistory());
  };

  const handleBackToForm = () => {
    setShowForm(true);
    setTask(null);
    setDoc(null);
    setError(null);
    setDocError(null);
    // Clear URL parameters
    window.history.pushState({}, '', window.location.pathname);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow py-8">
        <div className="max-w-4xl mx-auto px-4">
          {showForm ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={onBack}
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  ← Voltar para Toolbox
                </button>
              </div>
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <FileSearch className="w-12 h-12 text-blue-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Gerador de Resumo de Ações do e-Hub
                </h1>
                <p className="text-gray-600">
                  Insira os IDs da tarefa e do documento para gerar um relatório detalhado
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <ReportForm
                  onSubmit={generateReport}
                  onExampleClick={handleExampleClick}
                  loading={loading}
                  defaultApiKey={env.defaultApiKey}
                  onApiKeyChange={setApiKey}
                />
              </div>

              {/* Histórico de Relatórios */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">
                  Histórico de Relatórios
                </h2>
                <ReportHistory
                  items={history.items}
                  onItemClick={handleHistoryItemClick}
                  onItemRemove={handleHistoryItemRemove}
                />
              </div>
            </>
          ) : (
            task && (
              <TaskReport
                task={task}
                doc={doc}
                onBackToForm={handleBackToForm}
              />
            )
          )}
        </div>
      </div>
      
      <footer className="py-4 bg-gray-800 text-white text-center print:hidden">
        <p className="text-sm">Desenvolvido pelo Hub de Engenharia da Zallpy</p>
      </footer>
    </div>
  );
}

export default ReportGenerator;